import { RowOrigin } from '../models/source-key.model'
import { ActionIdSets } from '../models/transaction-action.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/**
 * Row id -> request id mapping for `POST /flex-transactions/actions`.
 *
 * The request takes plain ids with no encoding and no `transaction_type` (PVAL-26291 CHANGE 2), and
 * those are exactly what {@link WorkbenchRow.rowId} already holds: `ROW_ID_FIELD` maps `api` ->
 * `transactionId` and `prom` -> `extTranId` (`transactions-query.model.ts`), applied in
 * `row.adapter.ts`. So the mapping is a partition by origin, not a derivation.
 *
 *  - `enfnTranIds` <= rows with origin `api`
 *  - `extTranIds`  <= rows with origin `cust` or `trad` — both Prometheus feeds map to the same id
 *    set. The Custody/Trading distinction matters only for the menu's pre-flight check, because the
 *    server derives PROM_CUSTODY_TRAN / PROM_TRADING_TRAN from its own `data_source_type` column.
 *  - `cw` rows are not part of this contract at all (no read endpoint, no id set) and are reported
 *    as {@link RowActionIdMapping.unsupported}.
 */

/**
 * The synthetic id `row.adapter.ts` mints (`${sourceKey}:${index}`) when a row came back missing its
 * identity field. It keeps such a row renderable and addressable in the grid, but it is NOT a server
 * id — posting it would either 404 the row or, worse, address someone else's transaction. Rows
 * carrying one must be excluded and the action blocked with a message, never posted.
 *
 * The adapter also sets {@link WorkbenchRow.syntheticRowId} explicitly; the pattern is the
 * belt-and-braces check for rows built by any other path.
 */
export const SYNTHETIC_ROW_ID_PATTERN = /^(?:api|prom|cw):\d+$/

/** Whether this row's `rowId` is a real server id, and therefore safe to put in a request. */
export function hasServerRowId(row: WorkbenchRow): boolean {
  return (
    row.syntheticRowId !== true &&
    row.rowId !== '' &&
    !SYNTHETIC_ROW_ID_PATTERN.test(row.rowId)
  )
}

/** Origins whose ids go into `extTranIds`. */
const EXT_ORIGINS: readonly RowOrigin[] = ['cust', 'trad']

export interface RowActionIdMapping extends ActionIdSets {
  enfnTranIds: string[]
  extTranIds: string[]
  /**
   * Rows excluded because their `rowId` is the adapter's synthetic fallback rather than a server id.
   * A non-empty list must block the action outright — dropping them silently would submit a
   * partial action the user did not ask for.
   */
  blocked: WorkbenchRow[]
  /**
   * Rows excluded because their origin has no id set in this contract (Clearwater). Out of scope
   * rather than an error, but still not submittable.
   */
  unsupported: WorkbenchRow[]
}

/**
 * Partition rows into the request's two id sets by origin.
 *
 * @param rows the rows an action is scoped to
 * @returns the two id sets plus the rows that could not be mapped
 */
export function mapRowsToActionIds(
  rows: readonly WorkbenchRow[],
): RowActionIdMapping {
  const mapping: RowActionIdMapping = {
    enfnTranIds: [],
    extTranIds: [],
    blocked: [],
    unsupported: [],
  }

  rows.forEach(row => {
    if (row.origin === 'cw') {
      mapping.unsupported.push(row)
      return
    }
    if (!hasServerRowId(row)) {
      mapping.blocked.push(row)
      return
    }
    if (row.origin === 'api') mapping.enfnTranIds.push(row.rowId)
    else if (EXT_ORIGINS.includes(row.origin))
      mapping.extTranIds.push(row.rowId)
  })

  return mapping
}
